import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  makeStyles,
  Typography
} from "@material-ui/core";
import { InfoOutlined } from "@material-ui/icons";
import DeleteIcon from "@material-ui/icons/Delete";
import CardTitle from "@saleor/components/CardTitle";
import Grid from "@saleor/components/Grid";
import Hr from "@saleor/components/Hr";
// import { BrandErrorFragment } from "@saleor/fragments/types/BrandErrorFragment";
import { FormChange } from "@saleor/hooks/useForm";
import useNotifier from "@saleor/hooks/useNotifier";
import ImageIcon from "@saleor/icons/Image";
import { commonMessages } from "@saleor/intl";
import classNames from "classnames";
import React from "react";
import Dropzone from "react-dropzone";
import { FormattedMessage, useIntl } from "react-intl";

import { CollectionDetailsPageFormData } from "../CollectionDetailsPage";

// import { BrandGroupFormData } from "../BrandGroupDetailsPage";

const useStyles = makeStyles(
  theme => ({
    card: {
      marginTop: theme.spacing(2),
      [theme.breakpoints.down("sm")]: {
        marginTop: 0
      }
    },
    descriptionLabel: {
      margin: "5px 0px 5px 0px"
    },
    fileField: {
      display: "none"
    },
    fileUploadField: {
      padding: "0!important"
    },
    guidelines: {
      fontWeight: "bold"
    },
    helpText: {
      gridColumnEnd: "span 4"
    },
    icon: {
      color: "rgba(255, 255, 255, 0.54)"
    },
    image: {
      height: "100%",
      objectFit: "contain",
      userSelect: "none",
      width: "100%"
    },
    imageContainer: {
      "&:hover, &.dragged": {
        "& $imageOverlay": {
          display: "block"
        }
      },
      background: "#ffffff",
      border: "1px solid #eaeaea",
      borderRadius: theme.spacing(),
      height: 140,
      margin: "auto",
      overflow: "hidden",
      // padding: theme.spacing(2),
      position: "relative"
      // width: 140
    },
    imageGridContainer: {
      position: "relative"
    },

    imageOverlay: {
      background: "rgba(0, 0, 0, 0.6)",
      cursor: "move",
      display: "none",
      height: "100%",
      left: 0,
      // padding: theme.spacing(2),
      position: "absolute",
      top: 0,
      width: "100%"
    },
    imageOverlayShow: {
      "&$imageOverlay": {
        alignItems: "center",
        display: "flex",
        justifyContent: "center"
      }
    },
    imageOverlayToolbar: {
      display: "flex",
      // left: "95%",
      position: "absolute",
      right: 0,
      top: 0
    },
    imageUpload: {
      height: "100%",
      left: 0,
      outline: 0,
      position: "absolute",
      top: 0,
      width: "100%"
    },
    imageUploadActive: {
      zIndex: 1
    },
    imageUploadIconActive: {
      display: "block"
    },
    imageflex: {
      // display: "flex"
    },
    info: {
      color: "#0937f4",
      fontSize: 17,
      fontWeight: "bold"
    },
    infoGuidelines: {
      fontSize: 13,
      marginLeft: "10px"
    },
    photosIcon: {
      height: "64px",
      margin: "0 auto",
      width: "64px"
    },
    root: {
      display: "grid",
      gridColumnGap: theme.spacing(2),
      gridRowGap: theme.spacing(2),
      gridTemplateColumns: "repeat(4, 1fr)",
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "repeat(3, 1fr)"
      },
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "repeat(2, 1fr)"
      }
    },
    rootDragActive: {
      opacity: 0.2
    },
    uploadButton: {
      display: "block"
    },
    uploadRoot: {
      fontSize: 12,
      fontWeight: 600,
      padding: "15px",
      textAlign: "center"
    }
  }),
  { name: "CollectionThumbnailLinkImage" }
);

interface CollectionThumbnailImageProps {
  data: CollectionDetailsPageFormData;
  errors;
  disabled: boolean;
  loading?: boolean;
  onChange: FormChange;
  onImageDelete?: () => void;
  onImageUpload?: (file: File) => void;
  onFileSizeError?: () => void;
  onThumbnailLinkImageUpload?: (file: File) => void;
}

const fileSizeLimit = 500000;

const CollectionThumbnailImage: React.FC<CollectionThumbnailImageProps> = props => {
  const classes = useStyles(props);
  const intl = useIntl();
  const upload = React.useRef(null);
  const notify = useNotifier();
  const [thumbnailLinkImage, setthumbnailLinkImage] = React.useState(null);
  const [image, setImage] = React.useState(null);
  const { data, loading, onChange, onThumbnailLinkImageUpload } = props;

  React.useEffect(() => {
    if (thumbnailLinkImage) {
      setImage(URL.createObjectURL(thumbnailLinkImage[0]));
      onThumbnailLinkImageUpload(thumbnailLinkImage[0]);
    } else {
      onThumbnailLinkImageUpload(null);
    }
  }, [thumbnailLinkImage]);

  const checkFile = e => {
    const file = e[0];

    if (file.size < fileSizeLimit) {
      if (
        file.type.includes("image/jpeg") ||
        file.type.includes("image/jpg") ||
        file.type.includes("image/png")
      ) {
        setthumbnailLinkImage(e);
        onThumbnailLinkImageUpload(e);
        onChange({
          target: {
            name: "thumbnailLinkImage",
            value: file
          }
        });
      } else {
        notify({
          status: "error",
          text:
            "The format is not acceptable. Check the format and upload again!"
        });
      }
    } else {
      notify({
        status: "error",
        text: "File size is too big! Try compressing.",
        title: "Can't Upload."
      });
    }
  };

  return (
    <Card className={classes.card}>
      <CardTitle
        title={intl.formatMessage({
          defaultMessage: "Image Thumbnail",
          description: "section header"
        })}
        toolbar={
          <>
            <Button
              onClick={() => upload.current.click()}
              disabled={loading}
              variant="text"
              color="primary"
              data-test="button-upload-image"
              className={classes.uploadButton}
            >
              {intl.formatMessage(commonMessages.uploadImage)}
            </Button>
            <input
              className={classes.fileField}
              id="fileUpload"
              onChange={event => checkFile(event.target.files)}
              type="file"
              ref={upload}
              accept="image/jpeg, image/jpg, image/png"
            />
          </>
        }
      />
      <div
        className={classNames(classes.imageGridContainer, {
          [classes.imageflex]:
            thumbnailLinkImage || data.thumbnailLinkImage?.url
        })}
      >
        <CardContent>
          <Dropzone onDrop={checkFile} disableClick={true}>
            {({ getRootProps, getInputProps }) => (
              <div {...getRootProps()}>
                <CardContent
                  className={classes.fileUploadField}
                  onClick={() => upload.current.click()}
                >
                  <input
                    {...getInputProps()}
                    id="fileUpload"
                    accept="image/jpeg, image/jpg, image/png"
                    onChange={e =>
                      e.target.files.length > 0 && checkFile(e.target.files)
                    }
                    multiple={false}
                    ref={upload}
                    type="file"
                  />
                  {thumbnailLinkImage || data.thumbnailLinkImage?.url ? (
                    <div className={classes.imageContainer}>
                      <div
                        className={classNames(classes.imageOverlay, {
                          [classes.imageOverlayShow]: loading
                        })}
                      >
                        {loading ? (
                          <CircularProgress size={32} />
                        ) : (
                          <div className={classes.imageOverlayToolbar}>
                            <IconButton
                              color="primary"
                              onClick={() => {
                                upload.current.value = null;
                                setImage(null);
                                setthumbnailLinkImage(null);
                                onChange({
                                  target: {
                                    name: "thumbnailLinkImage",
                                    value: null
                                  }
                                });
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </div>
                        )}
                      </div>
                      <img
                        className={classes.image}
                        src={
                          thumbnailLinkImage
                            ? image
                            : data.thumbnailLinkImage?.url
                        }
                      />
                    </div>
                  ) : (
                    <div className={classes.uploadRoot}>
                      <ImageIcon className={classes.photosIcon} />
                      <p className={classes.descriptionLabel}>
                        <FormattedMessage
                          defaultMessage="DROP IMAGE HERE TO UPLOAD"
                          description="description"
                        />
                      </p>
                    </div>
                  )}
                </CardContent>
              </div>
            )}
          </Dropzone>
        </CardContent>
        <Hr />
        <CardContent>
          <Grid>
            <div
              style={{
                alignItems: "center",
                display: "flex",
                flexWrap: "wrap"
              }}
            >
              <InfoOutlined color="primary" />
              <Typography className={classes.info}>
                {" "}
                Upload Guidelines:
              </Typography>
            </div>
          </Grid>
          <div>
            <span className={classes.infoGuidelines}>
              {" "}
              1. Image can be{" "}
              <span className={classes.guidelines}>
                640 pixels to 1080 pixels in Width or Height.
              </span>{" "}
            </span>
            <br />
            <span className={classes.infoGuidelines}>
              {" "}
              2. Image file should{" "}
              <span className={classes.guidelines}>
                not be greater than 500KB.
              </span>
            </span>
            <br />
            <span className={classes.infoGuidelines}>
              {" "}
              3. Only{" "}
              <span className={classes.guidelines}>
                JPG, JPEG, or PNG files
              </span>{" "}
              are acceptable.
            </span>
            <br />
          </div>
        </CardContent>
      </div>
    </Card>
  );
};
CollectionThumbnailImage.displayName = "CollectionThumbnailImage";
export default CollectionThumbnailImage;
